import React, { useCallback, useContext, useState } from 'react';

import { motion } from 'framer-motion';
import { generatePath, NavLink } from 'react-router-dom';

import { Avatar } from '@/components/Avatar';
import { ProductType, resendInvitation, revokeAccess, User } from '@/core/api/collections/users';
import { AuthContext } from '@/core/auth/AuthContext';
import OffDutyChip from '@/core/users/common/OffDutyChip';
import { LicenseTypeLabel } from '@/core/users/UserManagement/LicenseTypeLabel';
import { UserRowActionMenu } from '@/core/users/UserManagement/UserRowActionMenu';
import routes from '@/routes';
import { useSnackbar } from '@/SnackbarProvider';

const HOVER_CLASSES = 'border-gray-400 border-t border-b bg-white shadow-form transition-colors';

type Props = {
  user: User;
  preventMotion?: boolean;
}
export function UserRow ({ user, preventMotion }: Props) {
  const { currentUser, hasAccessTo } = useContext(AuthContext);
  const { enqueueSnackbarError, enqueueSnackbar } = useSnackbar();
  const [isHovering, setIsHovering] = useState(false);
  const [isOffDuty, setIsOffDuty] = useState(user.permissions?.core?.offDuty);
  const [hasCoreAccess, setHasCoreAccess] = useState(user.permissions.core?.state !== 'deleted' && user.permissions.core);
  const [hasUvkAccess, setHasUvkAccess] = useState(user.permissions.uvk?.state !== 'deleted' && user.permissions.uvk?.admin);

  const navLinkProps = {
    to: generatePath(routes.users.permissions, { userId: user.id }),
  };
  const hasTicketing = hasAccessTo('ticketing_system');
  const invited = user.permissions.core?.state === 'invited' || user.permissions.uvk?.state === 'invited';
  const canResendInvite = currentUser!.coreOwner || currentUser!.uvkAdmin;

  const handleRevokeAccess = useCallback((product: ProductType) => {
    if (product === 'discovery') {
      setHasCoreAccess(false);
    } else {
      setHasUvkAccess(false);
    }
    revokeAccess(user, product).then(() => {
      enqueueSnackbar('Access removed', { variant: 'success'});
    }).catch((e) => {
      enqueueSnackbarError(e);
    });
  }, []);

  const onClickInlineResendInvitation = useCallback(async (evt: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    evt.preventDefault();
    resendInvitation(user.id).then(() => {
      enqueueSnackbar('Invitation sent!', { variant: 'success' });
    }).catch((e) => {
      enqueueSnackbarError(e);
    });
  }, []);

  return (!hasCoreAccess && !hasUvkAccess) ? null : (
    <motion.tr
      key={user.id}
      layout={!preventMotion}
      exit={{ opacity: 0 }}
      className="cursor-pointer"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <td>
        <NavLink
          {...navLinkProps}
          className={`flex items-center rounded-l border-l ${isHovering ? HOVER_CLASSES : ''}`}
        >
          <div className="w-10 h-10 mr-6">
            <Avatar user={user} />
          </div>
          <div className="flex flex-col">
            <div
              className="text-sm font-bold truncate"
              title={user.displayName}
              style={{ width: 330 }}
            >
              {user.displayName}
            </div>
            <div className="text-xs font-medium">{user.email}</div>
          </div>
        </NavLink>
      </td>
      <td>
        <NavLink className={`flex text-sm ${isHovering ? HOVER_CLASSES : ''}`} {...navLinkProps} tabIndex={-1}>
          <div className="flex items-center">
            <LicenseTypeLabel
              licenseType={user.permissions?.core?.licenseType}
              invited={invited}
              uvkAdmin={user.permissions.uvk?.admin}
            />
            {hasTicketing && isOffDuty && !invited && (
              <OffDutyChip />
            )}
            {canResendInvite && invited && isHovering && (
              <span className="ml-3 text-xs underline cursor-pointer" onClick={onClickInlineResendInvitation}>
                Resend invite
              </span>
            )}
          </div>
        </NavLink>
      </td>
      <td>
        <NavLink className={`flex items-center text-sm ${isHovering ? HOVER_CLASSES : ''}`} {...navLinkProps} tabIndex={-1}>
          {[
            hasCoreAccess ? 'UserVoice' : [],
            hasUvkAccess ? 'Validation' : [],
          ].flat().join(', ')}
        </NavLink>
      </td>
      <td className="p-0 text-right">
        <div className={`rounded-r border-r ${isHovering ? HOVER_CLASSES : ''}`}>
          <UserRowActionMenu
            user={user}
            isOffDuty={isOffDuty}
            onOffDutyToggled={hasTicketing && !invited ? () => setIsOffDuty(!isOffDuty) : undefined}
            onRevokeAccess={handleRevokeAccess}
            hasCoreAccess={!!hasCoreAccess}
            hasUvkAccess={!!hasUvkAccess}
          />
        </div>
      </td>
    </motion.tr>
  );
}
